'use client';
import React, { useEffect, useState } from 'react';
import { useFilter, useTranslation } from '@/hook';
import Image from 'next/image';
import {
    CarStatus,
    CarStatusText,
    defaultYearFilter,
    FILTER_NAMES,
    FilterDisplayItemData,
    FilterTabsInterface,
    IClientRetailFilters,
    ISearchTerms,
    KILOMETERS,
    MakeFilterUiOject,
    TRANSMISSION,
    YEAR_FILTER_DATA,
} from '@/types';
import SwipeableEdgeDrawerAnchored from '../shared/bottom-sheet/bottom-sheet-anchored.component';
import Typography from '@/components/shared/typography/typography.component';
import { CloseButtonIcon } from '../../../public/Images';
import {
    ClearAllTag,
    FilterGroup,
    FilterTabItem,
} from '@/components/filters/index';
import {
    cloneClientRetailFilters,
    convertClientToEsFilters,
    formatBrandName,
    slugify,
} from '@/utils/filterUtil';
import { numberWithCommas } from '@/utils';
import { getCarListingsElastic } from '@/api-requests/car-listing-elastic.apis';
import useDebounce from '@/hook/debounce.hook';

interface TFiltersMobileProps {
    applyFiltersClicked: (filters: IClientRetailFilters) => void;
    clearFiltersClicked: () => void;
    open: boolean;
    setOpen: (flag: boolean) => void;
    filterSelected: string;
    displayedCount: number;
    searchTerms: ISearchTerms;
    localFilters: IClientRetailFilters;
}
const FiltersMobile = ({
    applyFiltersClicked,
    open,
    setOpen,
    filterSelected,
    localFilters,
    displayedCount,
    searchTerms,
}: TFiltersMobileProps) => {
    const { t } = useTranslation();
    const [tempFilters, setTempFilters] = useState(
        cloneClientRetailFilters(localFilters)
    );
    const [tempResCount, setTempResCount] = useState(displayedCount);
    const {
        clearFilters,
        makeModelValueChanged,
        kilometersValueChanged,
        bodyTypeValueChanged,
        transmissionValueChanged,
        localFilters: internalFilters,
        priceRangeChanged,
        discountedValueToggle,
        emiRangeChanged,
        yearRangeChanged,
        availableValueChanged,
    } = useFilter(searchTerms, tempFilters, setTempFilters);

    const updateTotalResCount = useDebounce(() => {
        getCarListingsElastic({
            filters: convertClientToEsFilters(tempFilters, searchTerms),
        })
            .then(newCars => {
                if (newCars && 'total' in newCars) {
                    setTempResCount(newCars.total);
                }
            })
            .catch(err => {
                // eslint-disable-next-line no-console
                console.log(err);
            });
    }, 300);

    useEffect(() => {
        updateTotalResCount();
    }, [tempFilters]);

    const applyFilters = () => {
        applyFiltersClicked({ ...internalFilters });
    };

    const filterTabs: FilterTabsInterface[] = [
        {
            text: t(FILTER_NAMES.PRICE),
            selected: true,
            id: FILTER_NAMES.PRICE,
            count: 0,
        },
        {
            text: t(FILTER_NAMES.DISCOUNTED),
            selected: true,
            id: FILTER_NAMES.DISCOUNTED,
            count: 0,
        },
        {
            text: t(FILTER_NAMES.MAKE_MODEL),
            selected: false,
            id: FILTER_NAMES.MAKE_MODEL,
            count: 0,
        },
        {
            text: t(FILTER_NAMES.EMI),
            selected: false,
            id: FILTER_NAMES.EMI,
            count: 0,
        },
        {
            text: t(FILTER_NAMES.AVAILABLE),
            selected: false,
            id: FILTER_NAMES.AVAILABLE,
            count: 0,
        },
        {
            text: t(FILTER_NAMES.MODEL_YEAR),
            selected: false,
            id: FILTER_NAMES.MODEL_YEAR,
            count: 0,
        },

        {
            text: t(FILTER_NAMES.KILOMETERS),
            selected: false,
            id: FILTER_NAMES.KILOMETERS,
            count: 0,
        },
        {
            text: t(FILTER_NAMES.TRANSMISSION),
            selected: false,
            id: FILTER_NAMES.TRANSMISSION,
            count: 0,
        },
        {
            text: t(FILTER_NAMES.BODY_STYLE),
            selected: false,
            id: FILTER_NAMES.BODY_STYLE,
            count: 0,
        },
    ];

    const [filterTabsState, setFilterTabsState] =
        useState<FilterTabsInterface[]>(filterTabs);

    useEffect(() => {
        tabClicked(filterSelected);
    }, [filterSelected, open]);

    const tabClicked = (id: string | number) => {
        const modifiedTabs = filterTabsState.map(item => {
            return { ...item, selected: item.text === id || item.id === id };
        });
        setFilterTabsState(modifiedTabs);
    };

    return (
        <SwipeableEdgeDrawerAnchored
            open={open}
            setOpen={setOpen}
            key={`mobile-filter-${open}`}
        >
            <div className={'h-[83vh] relative'}>
                <div className={'mb-3.5 flex items-center justify-between'}>
                    <Typography
                        style={{
                            color: '#344054',
                        }}
                        className={'text-gray-700'}
                        variant={'body2Medium'}
                    >
                        {t('Filters')}
                    </Typography>
                    <Image
                        onClick={() => {
                            setOpen(false);
                        }}
                        loading={'lazy'}
                        width={CloseButtonIcon.width * 1.5}
                        height={CloseButtonIcon.height * 1.5}
                        src={CloseButtonIcon.src}
                        alt="close"
                    />
                </div>

                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <div>
                        {filterTabsState?.map(item => (
                            <div
                                key={`${item.id}-${item.selected}`}
                                className={'mb-4'}
                            >
                                <FilterTabItem
                                    text={item.text}
                                    selected={item.selected}
                                    id={item.id}
                                    tabClicked={tabClicked}
                                    count={item.count}
                                />
                            </div>
                        ))}
                    </div>
                    <div style={{ width: '54%' }}>
                        {filterTabsState[0].selected && (
                            <FilterGroup
                                key={`price-${tempFilters.price.min}-${tempFilters.price.max}`}
                                label={t('price')}
                                data={{
                                    min: searchTerms.priceTerms.min,
                                    max: searchTerms.priceTerms.max,
                                }}
                                range={[
                                    Math.max(
                                        tempFilters.price.min,
                                        searchTerms.priceTerms.min
                                    ),
                                    Math.min(
                                        tempFilters.price.max,
                                        searchTerms.priceTerms.max
                                    ),
                                ]}
                                filterType="slider"
                                rangeChanged={priceRangeChanged}
                                vertical={true}
                            />
                        )}

                        {filterTabsState[1].selected && (
                            <label className="relative flex justify-between items-center text-xl mt-3">
                                <Typography variant={'body2Medium'}>
                                    {t('discountedFilterLabel')}
                                </Typography>
                                <input
                                    type="checkbox"
                                    onChange={discountedValueToggle}
                                    checked={tempFilters.discounted}
                                    className="absolute left-1/2 -translate-x-1/2 w-full h-full peer appearance-none rounded-md"
                                />
                                <span
                                    className="w-10 h-6 flex items-center flex-shrink-0 ml-2 p-0.5 bg-gray-300 rounded-full duration-300
                    ease-in-out peer-checked:bg-primary
                    after:w-5 after:h-5
                    after:bg-white after:rounded-full
                    after:shadow-md after:duration-300
                    en:peer-checked:after:translate-x-4 ar:peer-checked:after:-translate-x-4"
                                ></span>
                            </label>
                        )}
                        {filterTabsState[2].selected && (
                            <FilterGroup
                                label={t('Make & Model')}
                                data={Object.keys(
                                    searchTerms.makeModelTree
                                ).map(make => {
                                    return {
                                        text: formatBrandName(make),
                                        value: make,
                                        models: Object.keys(
                                            searchTerms.makeModelTree[make]
                                                .models
                                        ).map(
                                            model =>
                                                ({
                                                    text: searchTerms
                                                        .makeModelTree[make]
                                                        .models?.[model],
                                                    value: model,
                                                    selected: !!(
                                                        tempFilters.makeModel[
                                                            make
                                                        ]?.models?.[model] ||
                                                        false
                                                    ),
                                                } as FilterDisplayItemData)
                                        ),
                                        selected: !!tempFilters.makeModel[make],
                                    } as MakeFilterUiOject;
                                })}
                                filterType="multiSelect"
                                filterItemClicked={() => null}
                                makeModelClicked={makeModelValueChanged}
                            />
                        )}
                        {filterTabsState[3].selected && (
                            <FilterGroup
                                key={`emi-${tempFilters.emi.min}-${tempFilters.emi.max}`}
                                label={t('emi')}
                                data={{
                                    min: searchTerms.emiTerms.min,
                                    max: searchTerms.emiTerms.max,
                                }}
                                range={[
                                    Math.max(
                                        tempFilters.emi.min,
                                        searchTerms.emiTerms.min
                                    ),
                                    Math.min(
                                        tempFilters.emi.max,
                                        searchTerms.emiTerms.max
                                    ),
                                ]}
                                filterType="slider"
                                rangeChanged={emiRangeChanged}
                                vertical={true}
                            />
                        )}
                        {filterTabsState[4].selected && (
                            <FilterGroup
                                label={t('Car Status')}
                                data={Object.values(CarStatus).map(cs => {
                                    return {
                                        text: t(CarStatusText[cs]),
                                        value: cs,
                                        selected:
                                            tempFilters.status &&
                                            slugify(tempFilters.status) ===
                                                slugify(cs),
                                    } as FilterDisplayItemData;
                                })}
                                filterType="radiobox"
                                filterItemClicked={availableValueChanged}
                            />
                        )}
                        {filterTabsState[5].selected && (
                            <FilterGroup
                                label={t('Year')}
                                data={YEAR_FILTER_DATA}
                                filterType="fromTo"
                                rangeChanged={yearRangeChanged}
                                range={
                                    tempFilters.year.min !=
                                    defaultYearFilter.min
                                        ? tempFilters.year.max !=
                                          defaultYearFilter.max
                                            ? [
                                                  tempFilters.year.min,
                                                  tempFilters.year.max,
                                              ]
                                            : [tempFilters.year.min]
                                        : [0, 0]
                                }
                            />
                        )}
                        {filterTabsState[6].selected && (
                            <FilterGroup
                                label={t('kilometers')}
                                data={Object.values(KILOMETERS).map(km => {
                                    return {
                                        text: `${numberWithCommas(km)} ${t(
                                            'KM & Less'
                                        )}`,
                                        value: km,
                                        selected:
                                            tempFilters.kilometers ===
                                            Number(km),
                                    } as FilterDisplayItemData;
                                })}
                                filterType="radiobox"
                                filterItemClicked={kilometersValueChanged}
                            />
                        )}
                        {filterTabsState[7].selected && (
                            <FilterGroup
                                label={t('Transmission')}
                                data={Object.values(TRANSMISSION).map(t => {
                                    return {
                                        text: t,
                                        value: t,
                                        selected: !!tempFilters.transmission[t],
                                    } as FilterDisplayItemData;
                                })}
                                filterType="checkbox"
                                filterItemClicked={transmissionValueChanged}
                            />
                        )}
                        {filterTabsState[8].selected && (
                            <FilterGroup
                                label={t('Body Style')}
                                data={searchTerms.bodyStyleTerms.bodyStylesRaw.map(
                                    bt => {
                                        return {
                                            text: bt,
                                            value: bt,
                                            selected:
                                                !!tempFilters.bodystyle[bt],
                                        } as FilterDisplayItemData;
                                    }
                                )}
                                filterType="checkbox"
                                filterItemClicked={bodyTypeValueChanged}
                            />
                        )}
                    </div>
                </div>

                <div
                    style={{
                        position: 'fixed',
                        bottom: 0,
                        width: '90%',
                    }}
                >
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            height: '98px',
                            backgroundColor: 'white',
                            alignItems: 'center',
                        }}
                    >
                        <Typography
                            style={{
                                color: '#98A2B3',
                                fontSize: '14px',
                                fontWeight: '500',
                            }}
                            variant={'captionMedium'}
                        >{`${tempResCount} ${t('Cars Found')}`}</Typography>

                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '12px',
                                justifySelf: 'center',
                            }}
                        >
                            <ClearAllTag
                                tagClicked={clearFilters}
                                variant={'red'}
                            />
                            <button
                                onClick={() => applyFilters()}
                                disabled={tempResCount === 0}
                                style={{
                                    width: '77px',
                                    border:
                                        tempResCount === 0
                                            ? 'solid 1px #D0D5DD'
                                            : 'solid 1px #009B5D',
                                    outline: 'none',
                                    color: 'white',
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    backgroundColor:
                                        tempResCount === 0
                                            ? '#D0D5DD'
                                            : '#009B5D',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <Typography variant={'body2Medium'}>
                                    {t('Apply')}
                                </Typography>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </SwipeableEdgeDrawerAnchored>
    );
};

export default FiltersMobile;
